// TODO: Remove this eslint-disable once the error is fixed
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  Cradle,
  Recording,
  RecordingId,
  CreateRecordingProps,
  UpdateRecordingProps,
  Audio,
  FileId,
  FileEntity,
  RevelatorDistributionStatus,
} from '@solo/app-core';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { translate } from '@/app/i18n';
import { Button, Section, Flex, Text } from '@/app/ui/atoms';
import RecordingTableComponent from './RecordingTableComponent/RecordingTableComponent';
import { useCradle } from '@/app/contexts';
import { Modal } from '@/app/ui/molecules';
import { useBlocState } from '@/app/hooks';
import RecordingForm from '@/app/ui/organisms/RecordingForm/RecordingForm';

const TracksComponent = () => {
  const { id } = useParams();
  const { musicalWorksBloc } = useCradle<Cradle>();
  const musicalWorksState = useBlocState(musicalWorksBloc);

  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [addingRecording, setAddingRecording] = useState<boolean>(false);
  const [deletingRecording, setDeletingRecording] = useState<Recording | undefined>();
  const [audioFile, setAudioFile] = useState<File | undefined>();
  const [isDistributed, setIsDistributed] = useState<boolean>(false);

  const getRecordings = async () => {
    if (id === undefined) {
      return;
    }

    setRecordings([]);

    try {
      const musicalRelease = await musicalWorksBloc.getMusicalRelease(id);
      setRecordings(musicalRelease.recordings.sort((a, b) => a.orderPosition - b.orderPosition));
      const distributions = musicalRelease.distributionStatuses?.filter(
        (status) =>
          status.status === RevelatorDistributionStatus.Delivered ||
          status.status === RevelatorDistributionStatus.OnStore,
      );
      setIsDistributed(distributions?.length ? distributions.length > 0 : false);
    } catch (error) {
      toast.error(translate('TOAST.TRACK_ERROR'));
    }
  };

  useEffect(() => {
    getRecordings();
  }, [musicalWorksState]);

  async function getAudioInfo(audioId: FileId): Promise<FileEntity> {
    const audioInfo = await musicalWorksBloc.getAudioInfo(audioId);
    return audioInfo;
  }

  async function createRecording(recording: CreateRecordingProps) {
    if (!id) return;

    let resourceId: FileId = 'resourceId';
    if (audioFile) {
      try {
        resourceId = await musicalWorksBloc.uploadAudio(audioFile);
      } catch (error) {
        toast.error(translate('TOAST.CHANGES_ERROR'));
      }
    }

    try {
      await musicalWorksBloc.createRecording({ ...recording, audioFileId: resourceId }, id);
      toast.success(translate('TOAST.CHANGES_SAVED'));
    } catch (error) {
      toast.error(translate('TOAST.SAVING_ERROR'));
    }

    setAudioFile(undefined);
    getRecordings();
  }

  async function updateRecording(recording: UpdateRecordingProps, recordingId: RecordingId) {
    let resourceId: FileId | undefined;

    if (audioFile !== undefined) {
      try {
        resourceId = await musicalWorksBloc.uploadAudio(audioFile);
      } catch (error) {
        toast.error(translate('TOAST.CHANGES_ERROR'));
      }
    }

    try {
      await musicalWorksBloc.updateRecording(recordingId, {
        ...recording,
        audioFileId: resourceId || recording.audioFileId || 'resourceId',
      });
      toast.success(translate('TOAST.CHANGES_SAVED'));
    } catch (error) {
      toast.error(translate('TOAST.CHANGES_ERROR'));
    }

    setAudioFile(undefined);
    getRecordings();
  }

  async function deleteRecording() {
    if (!id || !deletingRecording) return;
    try {
      await musicalWorksBloc.deleteRecording(deletingRecording.id, id);
      toast.success(translate('TOAST.TRACK_DELETED'));
    } catch (error) {
      toast.error(translate('TOAST.CHANGES_ERROR'));
    }
    setDeletingRecording(undefined);
    getRecordings();
  }

  const reorderRecordings = async (recordingId: RecordingId, indexTo: number) => {
    if (!id) return;

    await musicalWorksBloc.reorderReleaseRecordings(id, recordingId, indexTo);

    getRecordings();
  };

  return (
    <>
      <Section>
        <Flex.Container flex={1} justifyContent="flex-end">
          {!isDistributed && (
            <Button
              $size="md"
              $outline
              $transparent
              $variant="primary"
              onClick={() => {
                setAddingRecording(true);
              }}
            >
              <Text.Paragraph style={{ padding: 0 }} fontWeight={600}>
                {translate('BUTTON.ADD_NEW_TRACK')}
              </Text.Paragraph>
            </Button>
          )}
        </Flex.Container>
      </Section>
      <Section my={10}>
        {recordings && (
          <RecordingTableComponent
            deleteRecording={(recording) => setDeletingRecording(recording)}
            getAudioInfo={(audioId) => getAudioInfo(audioId)}
            createRecording={(recording) => createRecording(recording)}
            updateRecording={(recording, recordingId) => updateRecording(recording, recordingId)}
            setNewAudio={(audio) => setAudioFile(audio)}
            changeOrder={reorderRecordings}
            recordings={recordings}
            isDistributed={isDistributed}
          />
        )}
      </Section>
      <Modal
        isOpen={addingRecording}
        onClose={() => setAddingRecording(false)}
        closeable
        backdropCloseable
        position="top"
        size="lg"
      >
        <Flex.Container flexDirection="column" gap="40px" padding="30px 90px 20px 90px">
          <RecordingForm
            closeForm={() => setAddingRecording(false)}
            getAudioInfo={(audioId) => getAudioInfo(audioId)}
            createRecording={(recording) => createRecording(recording)}
            updateRecording={(recording, recordingId) => updateRecording(recording, recordingId)}
            setNewAudio={(audio) => setAudioFile(audio)}
            existingTracksCount={recordings.length}
          />
        </Flex.Container>
      </Modal>
      <Modal
        isOpen={deletingRecording !== undefined}
        onClose={() => setDeletingRecording(undefined)}
        closeable
        position="center"
        size="sm"
      >
        <Flex.Container flexDirection="column" gap="40px" padding="10px 20px">
          <Text.LabelMedium>
            {translate('TRACKS.SURE_DELETE')} {deletingRecording?.title} ?
          </Text.LabelMedium>
          <Flex.Container style={{ marginLeft: 'auto' }} gap="20px">
            <Button
              $transparent
              $variant="primary"
              onClick={() => {
                setDeletingRecording(undefined);
              }}
            >
              <Text.SectionSubtitle>{translate('PROFILE_INFO.CANCEL')}</Text.SectionSubtitle>
            </Button>

            <Button
              $size="md"
              $variant="primary"
              style={{ width: '290px' }}
              onClick={() => {
                deleteRecording();
              }}
            >
              <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
                {translate('BUTTON.DELETE')}
              </Text.SectionSubtitle>
            </Button>
          </Flex.Container>
        </Flex.Container>
      </Modal>
    </>
  );
};

export default TracksComponent;
